import { useEffect, useRef, useState } from 'react';
import { EmojiButton, insertAtCaret, restoreCaret } from '@/components/emoji/EmojiButton';
import { Link, useNavigate } from 'react-router-dom';
import { useCanPlanJourneys } from '@/lib/navAccess';
import { TaskJourneys } from '@/features/fleet/TaskJourneys';
import { hereAsOpener, openerState } from '@/lib/opener';
import { useQuery } from '@tanstack/react-query';
import { useMarkTaskRead } from '@tradeoutlet/data';
import clsx from 'clsx';
import { Info, Eye, EyeOff, Hash, Link2, Paperclip, Trash2, X, FileText, Ban, MessageSquare, Truck, FolderInput, Lock, Unlock } from 'lucide-react';
import { taskHref, taskPrivacy, type CustomField, type TaskPriority, type UpdateTaskInput, UPLOAD_ACCEPT, uploadFailureText } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fileSize } from '@/lib/format';
import { useMembers, useProjects, useTeams, useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { openAttachment, uploadFile } from '@/lib/upload';
import { CloseLink, Button, ErrorBox, Field, HoverBubble, Input, Menu, MenuItem, Modal, Select, Spinner, Tabs, Textarea, ask } from '@/components/ui';
import { MessageBody } from '@/features/chat/MessageBody';
import { encodeMentions } from '@/features/chat/Composer';
import { TeamChips } from '@/features/people/TeamPicker';
import { AssigneePicker } from '@/features/people/AudiencePicker';
import { AreaSelect, BudgetInput, EstimateInput, MILESTONE_HELP, MoreSection } from '@/features/tasks/taskExtras';
import { BuildingSelect } from '@/features/buildings/common';
import { AssigneeStack, PRIORITIES, PriorityIcon, StatusDot, patchTaskInCache } from './taskUi';
import { RecurrenceEditor } from './RecurrenceEditor';
import { useUpdateTask } from './useTaskMutations';
import { useTranslation } from '@/lib/translate';
import { TranslateBar, TranslateLink } from '@/features/ai/TranslateBar';
import { ActivityFeed } from '@/components/ActivityFeed';
import { PRIVATE_HINT, PrivateBadge, PrivateDueFields } from '@/features/tasks/privateTasks';
import { TaskReminderButton, TaskReminderChip } from '@/features/tasks/TaskReminder';
import { TaskCommentItem } from '@/features/tasks/TaskCommentItem';
import { useLiveTaskComments, useTaskCommentActions } from '@/features/tasks/taskCommentActions';
import { confirmAssigneeChange } from '@/lib/confirmRemove';
import { CompletedBy } from '@/features/tasks/CompletedBy';
import { enterToSend } from '@/lib/enterToSend';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="grid grid-cols-[7.5rem_1fr] items-center gap-2 py-1"><div className="text-xs font-medium text-slate-500">{label}</div><div className="min-w-0 text-sm">{children}</div></div>;
}

function CustomFieldInput({ field, value, disabled, onSave }: { field: CustomField; value: unknown; disabled: boolean; onSave: (v: unknown) => void }) {
  const { members } = useMembers();
  switch (field.field_type) {
    case 'checkbox': return <input type="checkbox" disabled={disabled} checked={Boolean(value)} onChange={e => onSave(e.target.checked)} />;
    case 'select': return <Select disabled={disabled} value={String(value ?? '')} onChange={e => onSave(e.target.value || null)}><option value="">—</option>{field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>;
    case 'multi_select': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return <div className="flex flex-wrap gap-1">{field.options.map(o => <button key={o.value} disabled={disabled} onClick={() => onSave(arr.includes(o.value) ? arr.filter(x => x !== o.value) : [...arr, o.value])} className={clsx('rounded-full px-2 py-0.5 text-xs', arr.includes(o.value) ? 'bg-accent text-on-accent' : 'bg-slate-100 dark:bg-slate-800')}>{o.label}</button>)}</div>;
    }
    case 'user': return <Select disabled={disabled} value={String(value ?? '')} onChange={e => onSave(e.target.value || null)}><option value="">—</option>{members.map(m => <option key={m.user_id} value={m.user_id}>{m.display_name}</option>)}</Select>;
    // t176u: our date field's change event carries yyyy-MM-dd; its blur event only has the displayed text ("Thu 22 Oct 2026").
    case 'date': return <Input disabled={disabled} type="date" value={String(value ?? '')} onChange={e => e.target.value !== String(value ?? '') && onSave(e.target.value || null)} />;
    case 'number': return <Input disabled={disabled} type="number" defaultValue={value == null ? '' : String(value)} onBlur={e => onSave(e.target.value === '' ? null : Number(e.target.value))} />;
    default: return <Input disabled={disabled} type={field.field_type === 'url' ? 'url' : 'text'} defaultValue={String(value ?? '')} onBlur={e => e.target.value !== (value ?? '') && onSave(e.target.value || null)} />;
  }
}

export function TaskPanel({ taskId }: { taskId: string }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const { byId } = useMembers();
  const { byId: teamsById } = useTeams();
  const teamName = (id: string) => teamsById.get(id)?.name ?? 'this team';
  const openTask = useUi(s => s.openTask);
  const toast = useUi(s => s.toast);
  const updateTask = useUpdateTask();
  const task = useQuery({ queryKey: qk.task(taskId), queryFn: () => api.task(taskId) });
  useMarkTaskRead(ws, taskId, Boolean(task.data)); // t293u: opening a task marks it read for you
  const t = task.data;
  const project = useQuery({ queryKey: qk.project(t?.project_id ?? ''), queryFn: () => api.project(t!.project_id), enabled: Boolean(t) });
  const comments = useQuery({ queryKey: ['task-comments', taskId], queryFn: () => api.taskComments(taskId) });
  const commentActs = useTaskCommentActions(id => byId.get(id)?.display_name ?? 'someone');
  useLiveTaskComments(taskId, t?.project_id, t?.no_project);
  const activity = useQuery({ queryKey: ['task-activity', taskId, t?.version], queryFn: () => api.taskActivity(taskId), enabled: Boolean(t) });
  const [tab, setTab] = useState<'comments' | 'activity'>('comments');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const commentRef = useRef<HTMLTextAreaElement>(null);
  // t261u: who was just added in the Assignees picker on this task, so "add Tom, take Jane off" asks Reassign.
  const addedHere = useRef<string[]>([]);
  useEffect(() => { addedHere.current = []; }, [taskId]);
  const [pickAssignees, setPickAssignees] = useState(false);
  const [subtask, setSubtask] = useState('');
  const [depKey, setDepKey] = useState('');
  const [menu, setMenu] = useState(false);
  const [moving, setMoving] = useState(false);
  const navigate = useNavigate();
  const canPlanJourney = useCanPlanJourneys();
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (t) setTitle(t.title); }, [t?.id, t?.title]); // eslint-disable-line react-hooks/exhaustive-deps

  const p = project.data;
  const canEdit = p?.can_edit ?? (p?.my_role === 'owner' || p?.my_role === 'editor');
  const save = async (patch: Omit<UpdateTaskInput, 'version'>) => {
    if (!t) return;
    await updateTask(t, patch);
    // t263u: a subtask's change (done, who completed it) shows on its parent without a reload.
    if (t.parent_task_id) void queryClient.invalidateQueries({ queryKey: qk.task(t.parent_task_id) });
  };
  const refresh = () => { void task.refetch(); if (t) void queryClient.invalidateQueries({ queryKey: qk.tasks(t.project_id) }); };
  const close = () => {
    openTask(null);
    const url = new URL(location.href);
    if (url.searchParams.has('task')) { url.searchParams.delete('task'); history.replaceState(null, '', url); }
  };

  if (task.isLoading) return <aside className="flex w-[36rem] shrink-0 items-center justify-center border-l border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"><Spinner /></aside>;
  if (!t) return (
    <aside className="w-[36rem] shrink-0 border-l border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
      <div className="mb-4 flex justify-end"><CloseLink onClose={close} /></div><ErrorBox error={task.error ?? 'Task not found'} />
    </aside>
  );

  const status = p?.statuses.find(s => s.id === t.status_id);
  const watching = t.watcher_ids.includes(userId ?? '');

  const addComment = async () => {
    const picked = new Map<string, string>();
    for (const [id, m] of byId) picked.set(m.display_name, id);
    const { body, mentions } = encodeMentions(comment.trim(), picked);
    if (!body) return;
    try { await api.addTaskComment(t.id, { body, mentions }); setComment(''); void comments.refetch(); patchTaskInCache(t.project_id, { id: t.id, comment_count: t.comment_count + 1 }); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const addSubtask = async () => {
    if (!subtask.trim()) return;
    try { await api.createTask(t.project_id, { title: subtask.trim(), parent_task_id: t.id }); setSubtask(''); refresh(); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const addDependency = async () => {
    if (!ws || !depKey.trim()) return;
    try {
      const r = await api.tasksByKeys(ws, [depKey.trim().toUpperCase()]);
      if (!r.tasks[0]) throw new Error(`No task ${depKey}`);
      await api.addDependency(t.id, r.tasks[0].id);
      setDepKey('');
      refresh();
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const upload = async (files: FileList) => {
    if (!ws) return;
    for (const f of Array.from(files)) {
      try { await uploadFile(f, { workspace_id: ws, task_id: t.id }); } catch (e) { toast(uploadFailureText(errorMessage(e), 'Upload failed'), 'error'); }
    }
    refresh();
  };
  /** Private <-> shared: only the creator, only a No project task with nobody else on it. */
  const canMakePrivate = taskPrivacy(t, userId).action === 'make_private';
  const setPrivate = async (on: boolean) => {
    setMenu(false);
    if (on && !await ask(`Make “${t.title}” private? Only you will see it; managers and admins won't. Other watchers are removed.`)) return;
    await save({ is_private: on });
    void queryClient.invalidateQueries({ queryKey: ['tasks-area'] });
    toast(on ? 'Private: only you can see this task' : 'Shared: you can add people, teams or a building now', 'success');
  };
  const remove = async () => {
    setMenu(false);
    if (!await ask(`Delete “${t.title}”? Subtasks are deleted too.`)) return;
    try { await api.deleteTask(t.id); patchTaskInCache(t.project_id, { id: t.id }, true); close(); toast(`Deleted: ${t.title}`, 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };

  return (
    <aside className="flex w-[36rem] shrink-0 flex-col border-l border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-950">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-800">
        {t.is_private ? <Link to="/tasks?visibility=private" className="text-sm text-slate-500 hover:text-slate-800">My private tasks</Link>
          : t.no_project ? <Link to="/tasks?project=none" className="text-sm text-slate-500 hover:text-slate-800" data-testid="task-no-project">No project</Link>
          : p && <Link to={`/projects/${p.id}`} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{p.name}</Link>}
        {t.is_private && <PrivateBadge />}
        <div className="ml-auto flex items-center gap-1">
          <Button size="icon" variant="ghost" title={watching ? 'Stop watching' : 'Watch'} onClick={() => void save({ watcher_ids: watching ? t.watcher_ids.filter(x => x !== userId) : [...t.watcher_ids, userId!] })}>{watching ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
          {canPlanJourney && <Button size="icon" variant="ghost" title="Plan delivery journey" onClick={() => { const from = hereAsOpener({ task: t.id }); close(); navigate(`/fleet/journeys/new?tasks=${t.id}`, { state: openerState(from) }); }} data-testid="task-plan-journey"><Truck className="h-4 w-4" /></Button>}
          <Button size="icon" variant="ghost" title="Copy link" onClick={() => { void navigator.clipboard.writeText(`${location.origin}${taskHref(t)}`); toast('Link copied', 'success'); }}><Link2 className="h-4 w-4" /></Button>
          {canEdit && <Menu open={menu} setOpen={setMenu} trigger={<Button size="icon" variant="ghost" title="More">⋯</Button>}>
            {!t.parent_task_id && !t.is_private && <MenuItem icon={<FolderInput className="h-4 w-4" />} onClick={() => { setMenu(false); setMoving(true); }}>{t.no_project ? 'Move to a project…' : 'Move to another project…'}</MenuItem>}
            {canMakePrivate && <MenuItem icon={<Lock className="h-4 w-4" />} onClick={() => void setPrivate(true)}>Make private (only you)</MenuItem>}
            {taskPrivacy(t, userId).action === 'make_shared' && <MenuItem icon={<Unlock className="h-4 w-4" />} onClick={() => void setPrivate(false)}>Make shared</MenuItem>}
            <MenuItem danger icon={<Trash2 className="h-4 w-4" />} onClick={() => void remove()}>Delete task</MenuItem>
          </Menu>}
          <TaskReminderButton taskId={t.id} />
          <CloseLink onClose={close} />
        </div>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {t.parent_task_id && <button onClick={() => openTask(t.parent_task_id)} className="mb-1 text-xs link">↑ Parent task</button>}
        {t.is_private && !t.parent_task_id && <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400" data-testid="private-note"><Lock className="h-3.5 w-3.5" aria-hidden />{PRIVATE_HINT}</p>}
        <TaskReminderChip taskId={t.id} />
        {t.reminder_for_task_id && <button onClick={() => openTask(t.reminder_for_task_id!)} className="mb-2 block text-xs link" data-testid="reminder-for">Open the task this reminds you about</button>}
        <textarea disabled={!canEdit} value={title} rows={1} onChange={e => setTitle(e.target.value)} onBlur={() => title.trim() && title !== t.title && void save({ title: title.trim() })}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); } }}
          className="w-full resize-none bg-transparent text-xl font-semibold outline-none focus:rounded focus-field" />

        <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
          <Row label="Status">
            <span className="flex items-center gap-2"><StatusDot status={status} />
              <Select disabled={!canEdit} className="h-8 py-1" value={t.status_id} onChange={e => void save({ status_id: e.target.value })}>{p?.statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
            </span>
          </Row>
          <Row label="Priority">
            <span className="flex items-center gap-2"><PriorityIcon priority={t.priority} />
              <Select disabled={!canEdit} className="h-8 py-1" value={t.priority} onChange={e => void save({ priority: e.target.value as TaskPriority })}>{PRIORITIES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}</Select>
            </span>
          </Row>
          {!t.is_private && <>
          <Row label="Assignees">
            {pickAssignees ? (
              <div className="space-y-2" data-testid="task-assignees-edit">
                <AssigneePicker assigneeIds={t.assignee_ids} teamIds={t.team_ids ?? []} onChange={v => void (async () => {
                  // t259u / t261u: the picker saves each change straight away, so taking someone (or a team) off asks first,
                  // and so does replacing them ("Reassign from Jane to Tom?"). Adding while keeping everyone doesn't ask.
                  const keys = (people: string[], teams: string[]) => [...people.map(id => `u:${id}`), ...teams.map(id => `t:${id}`)];
                  const label = (k: string) => (k.startsWith('t:') ? teamName(k.slice(2)) : byId.get(k.slice(2))?.display_name ?? 'this person');
                  const was = keys(t.assignee_ids, t.team_ids ?? []), next = keys(v.assignee_ids, v.team_ids);
                  if (!await confirmAssigneeChange(was, next, label, 'this task', addedHere.current)) return;
                  addedHere.current = [...addedHere.current, ...next.filter(k => !was.includes(k))].filter(k => next.includes(k));
                  await save(v);
                })()} />
                <Button size="sm" variant="secondary" onClick={() => setPickAssignees(false)}>Done</Button>
              </div>
            ) : (
              <button disabled={!canEdit} onClick={() => setPickAssignees(true)} className="flex flex-wrap items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="task-assignees">
                {t.team_ids?.length ? <TeamChips ids={t.team_ids} /> : null}
                {t.assignee_ids.length ? <><AssigneeStack ids={t.assignee_ids} max={5} /><span className="truncate text-sm">{t.assignee_ids.map(id => byId.get(id)?.display_name).join(', ')}</span></> : !t.team_ids?.length && <span className="text-slate-500">Unassigned</span>}
              </button>
            )}
          </Row>
          <Row label="Building">
            <BuildingSelect className="h-8 py-1" disabled={!canEdit} value={t.location_id} onChange={v => void save({ location_id: v })} />
          </Row>
          {t.location_id && <Row label="Area"><AreaSelect className="h-8 py-1" disabled={!canEdit} locationId={t.location_id} value={t.area_id} onChange={v => void save({ area_id: v })} /></Row>}
          </>}
          {t.is_private ? <Row label="Due">
            <PrivateDueFields disabled={!canEdit} value={{ due_date: t.due_date, due_time: t.due_time ?? null, reminder_minutes: t.reminder_minutes ?? null }} onChange={p => void save(p)} />
          </Row> : <Row label="Dates">
            <span className="flex items-center gap-2">
              <Input disabled={!canEdit} type="date" className="h-8 py-1" value={t.start_date ?? ''} onChange={e => void save({ start_date: e.target.value || null })} title="Start" />
              <span className="text-slate-500">→</span>
              <Input disabled={!canEdit} type="date" className="h-8 py-1" value={t.due_date ?? ''} onChange={e => void save({ due_date: e.target.value || null })} title="Due" />
            </span>
          </Row>}
          <Row label="Repeat"><RecurrenceEditor disabled={!canEdit} due={t.due_date} value={t.recurrence} onChange={r => void save({ recurrence: r })} /></Row>
          {!t.is_private && <div className="py-2"><MoreSection count={[t.milestone_id, t.estimate_minutes, t.budget_pence].filter(v => v != null).length}>
            {!t.no_project && <Row label="Milestone">
              <span className="flex items-center gap-1.5">
                <Select disabled={!canEdit} className="h-8 py-1" value={t.milestone_id ?? ''} onChange={e => void save({ milestone_id: e.target.value || null })} data-testid="task-milestone"><option value="">None</option>{p?.milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</Select>
                <HoverBubble bubble={MILESTONE_HELP} align="end"><Info className="h-3.5 w-3.5 text-slate-400" aria-label="What is a milestone?" tabIndex={0} data-testid="milestone-help" /></HoverBubble>
              </span>
            </Row>}
            <Row label="Estimate"><EstimateInput disabled={!canEdit} className="h-8 w-24 py-1" value={t.estimate_minutes} onChange={m => void save({ estimate_minutes: m })} /></Row>
            <Row label="Budget"><BudgetInput disabled={!canEdit} className="h-8 w-28 py-1" value={t.budget_pence} onChange={v => void save({ budget_pence: v })} /></Row>
          </MoreSection></div>}
          {p?.custom_fields.map(f => (
            <Row key={f.id} label={f.name}><CustomFieldInput field={f} value={t.custom_values[f.id]} disabled={!canEdit} onSave={v => void save({ custom_values: { ...t.custom_values, [f.id]: v } })} /></Row>
          ))}
        </div>

        <section className="mt-5">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Description</h3>
          {desc !== null ? (
            <div className="space-y-2">
              <Textarea autoFocus rows={6} value={desc} onChange={e => setDesc(e.target.value)} />
              <div className="flex gap-2"><Button size="sm" onClick={() => { void save({ description: desc }); setDesc(null); }}>Save</Button><Button size="sm" variant="ghost" onClick={() => setDesc(null)}>Cancel</Button></div>
            </div>
          ) : (
            <button disabled={!canEdit} onClick={() => setDesc(t.description)} className="block w-full rounded-md p-2 text-left hover:bg-slate-50 dark:hover:bg-slate-900">
              {t.description ? <MessageBody body={t.description} /> : <span className="text-sm text-slate-500">Add a description…</span>}
            </button>
          )}
        </section>

        <section className="mt-5">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Subtasks {t.subtasks.length > 0 && `(${t.subtasks.filter(s => s.completed_at).length}/${t.subtasks.length})`}</h3>
          <div className="space-y-1">
            {t.subtasks.map(s => (
              <div key={s.id} className="rounded px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-900" data-testid="subtask-row">
                <button type="button" onClick={() => openTask(s.id)} className="flex w-full items-center gap-2 text-left text-sm">
                  <StatusDot status={p?.statuses.find(x => x.id === s.status_id)} />
                  <span className={clsx('flex-1 truncate', s.completed_at && 'text-slate-500 line-through')}>{s.title}</span><AssigneeStack ids={s.assignee_ids} />
                </button>
                {/* t263u: who completed it and when (a sibling of the row's button, so the name opens the person). */}
                <CompletedBy t={s} className="pl-5" />
              </div>
            ))}
            {canEdit && <Input className="h-8 py-1" placeholder="+ Add subtask (Enter)" value={subtask} onChange={e => setSubtask(e.target.value)} onKeyDown={e => e.key === 'Enter' && void addSubtask()} />}
          </div>
        </section>

        <section className="mt-5">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Dependencies</h3>
          {t.depends_on.map(d => (
            <div key={d.id} className="flex items-center gap-2 py-0.5 text-sm">
              <Ban className={clsx('h-3.5 w-3.5', d.completed ? 'text-slate-300' : 'text-rose-500')} /><span className="text-xs text-slate-500">Blocked by</span>
              <button onClick={() => openTask(d.id)} className={clsx('truncate hover:underline', d.completed && 'line-through text-slate-500')}>{d.title}</button>
              {canEdit && <button className="ml-auto text-slate-500 hover:text-rose-600" aria-label={`Remove the wait on ${d.title ?? 'this task'}`} onClick={async () => { if (!await ask({ title: `Stop waiting on “${d.title}”?`, confirmLabel: 'Remove' })) return; await api.removeDependency(t.id, d.id); refresh(); }}><X className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
          {t.blocking.map(d => (
            <div key={d.id} className="flex items-center gap-2 py-0.5 text-sm"><span className="text-xs text-slate-500">Blocking</span><button onClick={() => openTask(d.id)} className="truncate hover:underline">{d.title}</button></div>
          ))}
          {canEdit && <div className="mt-1 flex gap-2"><Input className="h-8 py-1" placeholder="Blocked by task key, e.g. WH-3" value={depKey} onChange={e => setDepKey(e.target.value)} onKeyDown={e => e.key === 'Enter' && void addDependency()} /><Button size="sm" variant="secondary" onClick={() => void addDependency()}>Add</Button></div>}
        </section>

        {t.links.length > 0 && (
          <section className="mt-5">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Linked conversations</h3>
            {t.links.map(l => (
              <Link key={`${l.conversation_id}${l.message_id}`} to={`/c/${l.conversation_id}${l.message_id ? `#m-${l.message_id}` : ''}`} className="flex items-center gap-2 py-0.5 text-sm link">
                {l.message_id ? <MessageSquare className="h-3.5 w-3.5" /> : <Hash className="h-3.5 w-3.5" />}{l.conversation_title ?? 'Conversation'}
              </Link>
            ))}
          </section>
        )}

        {canPlanJourney && <TaskJourneys taskId={t.id} />}

        <section className="mt-5">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Attachments</h3>
            {canEdit && <button className="flex items-center gap-1 text-xs link" onClick={() => fileRef.current?.click()}><Paperclip className="h-3.5 w-3.5" />Upload</button>}
            <input ref={fileRef} type="file" accept={UPLOAD_ACCEPT} multiple hidden onChange={e => { if (e.target.files) void upload(e.target.files); e.target.value = ''; }} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {t.attachments.map(a => (
              <button key={a.id} onClick={() => openAttachment(a.id).catch(e => toast(errorMessage(e), 'error'))} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-left hover:border-accent-line dark:border-slate-700">
                <FileText className="h-6 w-6 text-accent-fg" /><span className="min-w-0"><span className="block truncate text-sm">{a.file_name}</span><span className="text-xs text-slate-500">{fileSize(a.size_bytes)}</span></span>
              </button>
            ))}
          </div>
          {!t.attachments.length && <p className="text-xs text-slate-500">No files.</p>}
        </section>

        <section className="mt-6">
          <Tabs value={tab} onChange={setTab} tabs={[{ id: 'comments', label: 'Comments', count: comments.data?.comments.filter(c => !c.deleted_at).length }, { id: 'activity', label: 'Activity' }]} />
          {tab === 'comments' ? (
            <div className="space-y-4 pt-3">
              {comments.data?.comments.filter(c => !c.deleted_at).map(c => (
                <TaskCommentItem key={c.id} id={`task-comment-${c.id}`} c={c} name={byId.get(c.author_id)?.display_name} mine={c.author_id === userId} act={commentActs}
                  body={<TranslatableComment id={c.id} body={c.body} mine={c.author_id === userId} />} />
              ))}
              <div className="space-y-2">
                <Textarea ref={commentRef} rows={2} placeholder="Write a comment…" value={comment} onChange={e => setComment(e.target.value)} onKeyDown={enterToSend({ value: comment, onSend: () => void addComment() })} />
                <div className="flex items-center justify-between">
                  <EmojiButton onPick={e => { const r = insertAtCaret(commentRef.current, comment, e); setComment(r.value); restoreCaret(commentRef.current, r.caret); }} />
                  <Button size="sm" disabled={!comment.trim()} onClick={() => void addComment()}>Comment</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="pt-1">
              <ActivityFeed rows={activity.data?.activity ?? []} refs={activity.data?.refs} nameOf={id => byId.get(id)?.display_name}
                onComment={id => {
                  setTab('comments');
                  // Scroll to the comment once the Comments tab has rendered, and flash it.
                  requestAnimationFrame(() => { const el = document.getElementById(`task-comment-${id}`); if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); el.classList.add('bg-slate-100', 'dark:bg-slate-800'); setTimeout(() => el.classList.remove('bg-slate-100', 'dark:bg-slate-800'), 1600); } });
                }} />
            </div>
          )}
        </section>
      </div>
      {moving && <MoveTaskModal taskId={t.id} current={t.no_project ? null : t.project_id} onClose={() => setMoving(false)} onMoved={() => { void task.refetch(); void queryClient.invalidateQueries({ queryKey: ['tasks-area'] }); void queryClient.invalidateQueries({ queryKey: qk.tasks(t.project_id) }); }} />}
    </aside>
  );
}

/** Task comment with translation (#27): auto when the reader turned it on, otherwise a small "Translate" link. */
function TranslatableComment({ id, body, mine }: { id: string; body: string; mine: boolean }) {
  const t = useTranslation('task_comment', id, { auto: !mine });
  return <><MessageBody body={t.translated?.text ?? body} /><TranslateBar t={t} />{!mine && <TranslateLink t={t} />}</>;
}

/** Move a task into a project, to another one, or out to No project (backlog #47). */
function MoveTaskModal({ taskId, current, onClose, onMoved }: { taskId: string; current: string | null; onClose: () => void; onMoved: () => void }) {
  const toast = useUi(s => s.toast);
  const projects = (useProjects().data?.projects ?? []).filter(p => !p.archived_at && (p.my_role === 'owner' || p.my_role === 'editor') && p.id !== current);
  const [to, setTo] = useState<string>(current ? '' : projects[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.moveTask(taskId, to || null); toast('Moved', 'success'); onMoved(); onClose(); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Move task"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!current && !to} onClick={() => void go()}>Move</Button></>}>
      <div className="space-y-3">
        <Field label="Move to">
          <Select value={to} onChange={e => setTo(e.target.value)}>
            {current && <option value="">No project</option>}
            {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.key})</option>)}
          </Select>
        </Field>
        <p className="text-xs text-slate-500">The task and its subtasks get a new number. Milestones and custom fields belong to a project, so they're cleared. {to ? 'Everyone who can see the project will see it.' : 'With no project, only the people it\'s for can see it.'}</p>
      </div>
    </Modal>
  );
}
